import React from 'react';
import { Field, Form, Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Category, Color } from '../../../../../server-types';
import { InputField, SelectField } from '../../../../../components/forms/fields';
import { mapEnumOptions, mapOptions } from '../../../../../utils';
import { ColorInput, ColorMarker } from '../style';
import { Typography } from '../../../../../ui-kit';
import { createCategoryValidationSchema } from './validation';
import { CreateCategoryFormProps, CreateCategorySubmitValues, CreateCategoryValues, FormMode } from './types';
import { ChildCategoriesInput } from './ChildCategoriesInput';

const CreateCategoryFormView = ({
    submitText,
    onSubmit,
    categories,
    initialValues = {
        name: '',
        color: Color.Purple,
        childCategories: [],
        parentCategoryId: '',
        formMode: 'indeterminate',
    },
}: CreateCategoryFormProps) => {

    const handleSubmit = async (values: CreateCategoryValues) => {
        const { name, color, parentCategoryId, childCategories, formMode } = values;

        let submitValues: CreateCategorySubmitValues | undefined;

        if(formMode === 'parent') {
            const sanitizedChildArray = childCategories.reduce((acc: string[], child) => {
                if(child.name !== ''){
                    acc.push(child.name);
                }
                return acc;
            },[]);
            submitValues = { name, color, childCategories: sanitizedChildArray };
        } else if(formMode === 'child') {
            submitValues = { name, parentCategoryId };
        } else {
            submitValues = { name, color };
        }

        await onSubmit(submitValues);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={createCategoryValidationSchema}
        >
            {formikProps => <CategoryForm categories={categories} formikProps={formikProps} submitText={submitText} />}
        </Formik>
    );
};

export default CreateCategoryFormView;

interface CatagoryFormProps {
    formikProps: FormikProps<CreateCategoryValues>;
    submitText: string;
    categories: Category[];
}

const CategoryForm: React.FC<CatagoryFormProps> = ({ formikProps, submitText, categories }) => {
    const { t } = useTranslation();
    const categoriesOptions = mapOptions(categories);
    const categoryColorOptions = mapEnumOptions(t)(Color);
    const [formMode, setFormMode] = React.useState<FormMode>('indeterminate');

    const { values: { childCategories, color }, setFieldValue } = formikProps;
    React.useEffect(() => {
        if(formMode === 'indeterminate' && childCategories.length === 0){
            return;
        }

        if(childCategories.length > 0 && formMode !== 'child') {
            setFormMode('parent');
            setFieldValue('formMode', 'parent');
        } else if(childCategories.length === 0 && formMode !== 'child'){
            setFormMode('indeterminate');
            setFieldValue('formMode', 'indeterminate');
        }
    }, [childCategories, formMode, setFieldValue]);

    const showParentFields = (formMode === 'parent' || formMode === 'indeterminate');

    const toggleChildMode = () => {
        setFormMode('child');
        formikProps.setFieldValue('formMode', 'child');
    };
    const toggleParentMode = () => {
        setFormMode('parent');
        formikProps.setFieldValue('formMode', 'parent');
    };
    return (
        <Form>
            <Box display='flex' alignItems='center'>
                {(color && showParentFields)
                ? (
                    <ColorMarker mr={1.5} color={color} />
                ) : null}
                <InputField<CreateCategoryValues>
                    name="name"
                    label={t('Name')}
                    formikProps={formikProps}
                    required
                />
            </Box>
            {showParentFields
            ? (
                <>
                    <Typography uppercase paragraph variant='caption'>{t('category color')}</Typography>
                    <Box mx={-0.5} display='flex'>
                        {categoryColorOptions.map(({ id, name }) => (
                            <Field
                                key={id}
                                as={ColorInput}
                                type="radio"
                                name="color"
                                value={name}
                                formikProps={formikProps}
                            />
                        ))}
                    </Box>
                </>
            )
            : null}
            <Box my={1.5} display={formMode === 'indeterminate' ? 'flex' : 'block'}>
                {showParentFields
                ? (
                    <ChildCategoriesInput
                        name='childCategories'
                    />
                ): null}
                {formMode === 'indeterminate'
                    ? (
                        <Box ml={5}>
                            <Button color='primary' onClick={toggleChildMode}>{t('Attach to a parent')}</Button>
                        </Box>
                    ) : null}
                {formMode === 'child'
                ? (
                    <Box display='flex' justifyContent='space-between'>
                        <Typography>{t('Attach to a parent')}</Typography>
                        <IconButton
                            color='primary'
                            onClick={toggleParentMode}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                ): null}
            </Box>
            {formMode === 'child'
            ? (
                <SelectField
                    showEmpty
                    variant='outlined'
                    options={categoriesOptions}
                    name='parentCategoryId'
                    label={t('Parent Category')}
                    formikProps={formikProps}
                />
            ) : null}
            <Box my={3}>
                <Divider />
            </Box>
            <Field
                type='hidden'
                name='formMode'
                value={formMode}
            />
            <Box display='flex' mt={1.5} justifyContent='flex-end'>
                <Button
                    disabled={formikProps.isSubmitting}
                    type="submit"
                    variant="contained"
                    color="primary"
                >
                    {submitText}
                </Button>
            </Box>
        </Form>
    );
};